import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ApprovalWithDetails } from '../../../core/models';
import { StatusBadgeComponent } from '../status-badge/status-badge.component';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';

@Component({
  selector: 'app-approval-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, StatusBadgeComponent, UserAvatarComponent, TranslateModule],
  template: `
    <article class="approval-card">
      <header class="card-header">
        <app-user-avatar
          [name]="approval().requesterName"
          [src]="approval().requesterAvatar"
          size="md" />
        <div class="header-content">
          <h3 class="requester-name">{{ approval().requesterName }}</h3>
          <p class="request-type">{{ ('approvals.type.' + approval().type) | translate }}</p>
        </div>
        <app-status-badge [status]="approval().status" />
      </header>

      <div class="card-body">
        <p class="summary">{{ approval().summary }}</p>
        @if (approval().dueDate) {
          <p class="due-date">
            {{ 'approvals.due' | translate }}: {{ approval().dueDate | date:'mediumDate' }}
          </p>
        }
      </div>

      @if (approval().status === 'pending') {
        <footer class="card-actions">
          <button
            type="button"
            class="btn btn-secondary"
            (click)="onReject()">
            {{ 'common.reject' | translate }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            (click)="onApprove()">
            {{ 'common.approve' | translate }}
          </button>
        </footer>
      }
    </article>
  `,
  styles: `
    .approval-card {
      background: white;
      border: 1px solid oklch(0.92 0 0);
      border-radius: 0.75rem;
      overflow: hidden;
    }

    .card-header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 1rem;
      background: oklch(0.98 0 0);
      border-bottom: 1px solid oklch(0.92 0 0);
    }

    .header-content {
      flex: 1;
      min-width: 0;
    }

    .requester-name {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: oklch(0.25 0 0);
    }

    .request-type {
      margin: 0.125rem 0 0;
      font-size: 0.8125rem;
      color: oklch(0.5 0 0);
      text-transform: capitalize;
    }

    .card-body {
      padding: 1rem;
    }

    .summary {
      margin: 0;
      font-size: 0.875rem;
      color: oklch(0.35 0 0);
      line-height: 1.5;
    }

    .due-date {
      margin: 0.75rem 0 0;
      font-size: 0.75rem;
      color: oklch(0.5 0 0);
    }

    .card-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      background: oklch(0.98 0 0);
      border-top: 1px solid oklch(0.92 0 0);
    }

    .btn {
      padding: 0.5rem 1rem;
      border-radius: 0.5rem;
      font-size: 0.8125rem;
      font-weight: 500;
      cursor: pointer;
      border: none;
      transition: background-color 0.15s;
    }

    .btn-secondary {
      background: white;
      color: oklch(0.35 0 0);
      border: 1px solid oklch(0.85 0 0);
    }

    .btn-secondary:hover {
      background: oklch(0.97 0 0);
    }

    .btn-primary {
      background: oklch(0.5 0.2 250);
      color: white;
    }

    .btn-primary:hover {
      background: oklch(0.45 0.2 250);
    }

    .btn:focus-visible {
      outline: 2px solid oklch(0.5 0.2 250);
      outline-offset: 2px;
    }
  `,
})
export class ApprovalCardComponent {
  readonly approval = input.required<ApprovalWithDetails>();

  readonly approve = output<void>();
  readonly reject = output<void>();

  protected onApprove(): void {
    this.approve.emit();
  }

  protected onReject(): void {
    this.reject.emit();
  }
}
